import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { isObject, validationErrorsToString } from '../../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';

// ICONS
import { ReactComponent as AlertIcon } from '../../../assets/svg/alert.svg';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';

interface ApproveDeleteOrganisationModalTypes {
	organisationId?: string;
	closeModal?: closeModalType;
}

const ApproveDeleteOrganisationModal: React.FC<ApproveDeleteOrganisationModalTypes> = ({
	organisationId,
	closeModal
}) => {
	const history = useHistory();

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [confirmation, setConfirmation] = React.useState<string>('');

	const canDelete = confirmation === 'DELETE';

	const confirmDelete = async () => {
		if (!organisationId || !canDelete) {
			return;
		}

		setIsLoading(true);

		const response = await apibridge.postOrganisationApproveDeletion({
			id: organisationId,
			confirmation: 'DELETE'
		});

		if (isObject(response) && response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}

			history.push('/companies');
		}
	};

	return (
		<div className="modal-confirmation delete-organisation-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<form
								onSubmit={(event) => {
									event.preventDefault();

									if (canDelete) {
										confirmDelete();
									}
								}}
							>
								<figure className="icon">
									<AlertIcon />
								</figure>

								<h2 className="heading h4 mbtom-20">
									To confirm the request to delete this organisation, please type <strong>DELETE</strong> in uppercase
									below:
								</h2>

								<p className="delete-organisation-warning body-medium mb-0">
									The deletion of an organisation will remove all organisation data from the ChildAware platform,
									including deletion of all associated files and assets.
								</p>

								<p className="delete-organisation-warning body-medium mb-0">
									Upon approval or decline, all Super Administrators will be notified of the outcome via email.
								</p>

								<input
									className="form-control delete-organisation-input text-center"
									type="text"
									value={confirmation}
									onChange={(event) => setConfirmation(event.target.value)}
									autoComplete="off"
								/>

								<section className="actions mtop-30">
									<Button className="btn-danger-solid full-width" disabled={!canDelete} onClick={confirmDelete}>
										Delete organisation
									</Button>

									<Button className="btn-secondary full-width" type="button" onClick={closeModal}>
										Cancel
									</Button>
								</section>
							</form>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default ApproveDeleteOrganisationModal;
